import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft, 
  Download, 
  Share2, 
  Edit, 
  Calendar,
  Filter,
  RefreshCw,
  BarChart3,
  PieChart,
  LineChart,
  Table,
  FileText,
  Clock,
  TrendingUp,
  TrendingDown,
  Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from '@/hooks/use-toast';

interface ReportData {
  id: string;
  name: string;
  description: string;
  createdAt: string;
  lastRun: string;
  status: 'running' | 'completed' | 'failed';
  widgets: any[];
  filters: any[];
  schedule?: {
    frequency: 'daily' | 'weekly' | 'monthly';
    nextRun: string;
  };
}

// Mock report data
const mockReport: ReportData = {
  id: '1',
  name: 'Production Efficiency Report',
  description: 'Comprehensive analysis of production efficiency metrics and KPIs',
  createdAt: '2024-01-10T10:00:00Z',
  lastRun: '2024-01-15T08:00:00Z',
  status: 'completed',
  widgets: [
    {
      id: 'widget1',
      type: 'chart',
      title: 'Production Volume by Month',
      chartType: 'bar',
      data: [
        { month: 'Jan', planned: 1000, actual: 950 },
        { month: 'Feb', planned: 1200, actual: 1180 },
        { month: 'Mar', planned: 1100, actual: 1050 },
        { month: 'Apr', planned: 1300, actual: 1250 },
      ]
    },
    {
      id: 'widget2',
      type: 'metric',
      title: 'Overall Efficiency',
      value: 94.2,
      unit: '%',
      trend: 'up',
      change: 2.3
    },
    {
      id: 'widget3',
      type: 'chart',
      title: 'Quality Score Distribution',
      chartType: 'pie',
      data: [
        { category: 'Excellent (90-100%)', value: 45 },
        { category: 'Good (80-89%)', value: 35 },
        { category: 'Fair (70-79%)', value: 15 },
        { category: 'Poor (<70%)', value: 5 },
      ]
    },
    {
      id: 'widget4',
      type: 'table',
      title: 'Top Performing Products',
      data: [
        { product: 'Widget A', efficiency: 98.5, defects: 2, volume: 500 },
        { product: 'Widget B', efficiency: 96.2, defects: 5, volume: 450 },
        { product: 'Widget C', efficiency: 94.8, defects: 8, volume: 400 },
        { product: 'Widget D', efficiency: 92.1, defects: 12, volume: 350 },
      ]
    }
  ],
  filters: [
    { id: 'dateRange', label: 'Date Range', value: 'Last 30 days' },
    { id: 'department', label: 'Department', value: 'All Departments' },
    { id: 'productLine', label: 'Product Line', value: 'All Products' },
  ],
  schedule: {
    frequency: 'weekly',
    nextRun: '2024-01-22T08:00:00Z'
  }
};

export default function ProductionReportDetail() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [report, setReport] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedDateRange, setSelectedDateRange] = useState('last30days');

  useEffect(() => {
    const fetchReport = async () => {
      try {
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        if (id === '1') {
          setReport(mockReport);
        } else {
          throw new Error('Report not found');
        }
      } catch (error) {
        toast({
          title: 'Error',
          description: 'Failed to load report. Please try again.',
          variant: 'destructive',
        });
        navigate('/manufacturing/reports');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchReport();
    }
  }, [id, navigate]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      // Simulate API call to refresh report data
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      toast({
        title: 'Report Refreshed',
        description: 'Report data has been updated with the latest information.',
      });
      
      // Update last run time
      if (report) {
        setReport({
          ...report,
          lastRun: new Date().toISOString()
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to refresh report data.',
        variant: 'destructive',
      });
    } finally {
      setRefreshing(false);
    }
  };

  const handleDownload = (format: 'pdf' | 'excel' | 'csv') => {
    toast({
      title: 'Download Started',
      description: `Report is being exported as ${format.toUpperCase()}. You'll be notified when ready.`,
    });
  };

  const handleShare = () => {
    // Copy report URL to clipboard
    const url = window.location.href;
    navigator.clipboard.writeText(url);
    
    toast({
      title: 'Link Copied',
      description: 'Report link has been copied to clipboard.',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'running': return 'bg-blue-100 text-blue-800';
      case 'failed': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const renderChart = (widget: any) => {
    if (widget.chartType === 'bar') {
      return (
        <div className="h-64 flex items-end justify-around p-4 bg-gray-50 rounded">
          {widget.data.map((item: any, index: number) => (
            <div key={index} className="flex flex-col items-center space-y-2">
              <div className="flex space-x-1">
                <div 
                  className="w-8 bg-blue-500 rounded-t"
                  style={{ height: `${(item.planned / 1500) * 150}px` }}
                  title={`Planned: ${item.planned}`}
                />
                <div 
                  className="w-8 bg-green-500 rounded-t"
                  style={{ height: `${(item.actual / 1500) * 150}px` }}
                  title={`Actual: ${item.actual}`}
                />
              </div>
              <span className="text-xs text-gray-600">{item.month}</span>
            </div>
          ))}
        </div>
      );
    }

    if (widget.chartType === 'pie') {
      return (
        <div className="h-64 flex items-center justify-center bg-gray-50 rounded">
          <div className="text-center">
            <PieChart className="h-24 w-24 mx-auto mb-4 text-gray-400" />
            <p className="text-sm text-gray-500">Pie Chart Visualization</p>
            <div className="mt-4 space-y-2">
              {widget.data.map((item: any, index: number) => (
                <div key={index} className="flex items-center justify-between text-xs">
                  <span>{item.category}</span>
                  <span className="font-medium">{item.value}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="h-64 flex items-center justify-center bg-gray-50 rounded">
        <div className="text-center text-gray-400">
          <BarChart3 className="h-12 w-12 mx-auto mb-2" />
          <p>Chart visualization</p>
        </div>
      </div>
    );
  };

  const renderMetric = (widget: any) => (
    <div className="text-center p-6">
      <div className="text-4xl font-bold text-gray-900 mb-2">
        {widget.value}{widget.unit}
      </div>
      <div className="flex items-center justify-center space-x-2">
        {widget.trend === 'up' ? (
          <TrendingUp className="h-4 w-4 text-green-500" />
        ) : (
          <TrendingDown className="h-4 w-4 text-red-500" />
        )}
        <span className={`text-sm ${widget.trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
          {widget.change > 0 ? '+' : ''}{widget.change}%
        </span>
        <span className="text-sm text-gray-500">vs last period</span>
      </div>
    </div>
  );

  const renderTable = (widget: any) => (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b bg-gray-50">
            <th className="text-left p-2">Product</th>
            <th className="text-left p-2">Efficiency</th>
            <th className="text-left p-2">Defects</th>
            <th className="text-left p-2">Volume</th>
          </tr>
        </thead>
        <tbody>
          {widget.data.map((row: any, index: number) => (
            <tr key={index} className="border-b hover:bg-gray-50">
              <td className="p-2 font-medium">{row.product}</td>
              <td className="p-2">{row.efficiency}%</td>
              <td className="p-2">{row.defects}</td>
              <td className="p-2">{row.volume}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  if (loading) {
    return (
      <div className="container mx-auto py-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin mr-2" />
              <span>Loading report...</span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="container mx-auto py-6">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <FileText className="h-12 w-12 mx-auto mb-4 text-gray-400" />
              <p className="text-gray-500">Report not found</p>
              <Button
                onClick={() => navigate('/manufacturing/reports')}
                className="mt-4"
              >
                Back to Reports
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/manufacturing/reports')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Reports
          </Button>
          <div>
            <h1 className="text-2xl font-bold">{report.name}</h1>
            <p className="text-gray-600">{report.description}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge className={getStatusColor(report.status)}>
            {report.status}
          </Badge>
          <span className="text-sm text-gray-500">
            Last updated: {new Date(report.lastRun).toLocaleString()}
          </span>
        </div>
      </div>

      {/* Report Info */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Report Information
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                disabled={refreshing}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                {refreshing ? 'Refreshing...' : 'Refresh'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/manufacturing/reports/${id}/edit`)}
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleShare}
              >
                <Share2 className="h-4 w-4 mr-2" />
                Share
              </Button>
              <Select onValueChange={(value) => handleDownload(value as any)}>
                <SelectTrigger className="w-32">
                  <Download className="h-4 w-4 mr-2" />
                  <span>Export</span>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pdf">PDF</SelectItem>
                  <SelectItem value="excel">Excel</SelectItem>
                  <SelectItem value="csv">CSV</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="font-medium">Created:</span>
              <p className="text-gray-600">{new Date(report.createdAt).toLocaleDateString()}</p>
            </div>
            <div>
              <span className="font-medium">Last Run:</span>
              <p className="text-gray-600">{new Date(report.lastRun).toLocaleString()}</p>
            </div>
            <div>
              <span className="font-medium">Schedule:</span>
              <p className="text-gray-600 capitalize">
                {report.schedule ? `${report.schedule.frequency}` : 'Manual'}
              </p>
            </div>
            <div>
              <span className="font-medium">Next Run:</span>
              <p className="text-gray-600">
                {report.schedule ? new Date(report.schedule.nextRun).toLocaleString() : 'N/A'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Report Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Date Range</Label>
              <Select value={selectedDateRange} onValueChange={setSelectedDateRange}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="last7days">Last 7 days</SelectItem>
                  <SelectItem value="last30days">Last 30 days</SelectItem>
                  <SelectItem value="last90days">Last 90 days</SelectItem>
                  <SelectItem value="custom">Custom range</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Department</Label>
              <Select defaultValue="all">
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Departments</SelectItem>
                  <SelectItem value="production">Production</SelectItem>
                  <SelectItem value="quality">Quality Control</SelectItem>
                  <SelectItem value="maintenance">Maintenance</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Product Line</Label>
              <Select defaultValue="all">
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Products</SelectItem>
                  <SelectItem value="widgets">Widgets</SelectItem>
                  <SelectItem value="components">Components</SelectItem>
                  <SelectItem value="assemblies">Assemblies</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Report Widgets */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {report.widgets.map((widget) => (
          <Card key={widget.id}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {widget.type === 'chart' && <BarChart3 className="h-5 w-5" />}
                {widget.type === 'table' && <Table className="h-5 w-5" />}
                {widget.type === 'metric' && <TrendingUp className="h-5 w-5" />}
                {widget.title}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {widget.type === 'chart' && renderChart(widget)}
              {widget.type === 'metric' && renderMetric(widget)}
              {widget.type === 'table' && renderTable(widget)}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Report Schedule */}
      {report.schedule && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Schedule Information
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <span className="font-medium">Frequency:</span>
                <p className="text-gray-600 capitalize">{report.schedule.frequency}</p>
              </div>
              <div>
                <span className="font-medium">Next Run:</span>
                <p className="text-gray-600">{new Date(report.schedule.nextRun).toLocaleString()}</p>
              </div>
              <div>
                <span className="font-medium">Status:</span>
                <Badge className="ml-2">Active</Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}